import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { getSession, onAuthStateChange, signOut } from "@/api/auth";
import { fetchProfileMaybe, fetchUserRoles } from "@/api/profiles";
import { fetchEmployeeWithStoreInfo } from "@/api/employees";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "@supabase/supabase-js";
import type { Enums, Tables } from "@/integrations/supabase/types";
import { identifyPosthog, POSTHOG_OPT_IN_EVENT } from "@/lib/posthog";
import { setSentryUser, setSentryContext } from "@/lib/sentry";
import { logger } from "@/lib/logger";
import { reportError } from "@/lib/reportError";
import { canUseCrisp, disableCrisp, enableCrisp } from "@/lib/crisp";
import { clearAccountScopedCaches } from "@/lib/sessionCleanup";
import { EmployeeDashboardSkeleton } from "@/components/skeletons/EmployeeDashboardSkeleton";
import { SessionLoadError } from "@/components/SessionLoadError";
import { ManagerOnEmployeePortal } from "@/components/ManagerOnEmployeePortal";

type Employee = Tables<"employees">;

type SubscriptionStatus = "active" | "trial" | "grace" | "archived" | "pending_subscription";

interface EmployeeContextType {
  user: User | null;
  employee: Employee | null;
  userRole: Enums<"app_role"> | null;
  storeName: string | null;
  storeSubscriptionStatus: SubscriptionStatus;
  isLoading: boolean;
  handleLogout: () => Promise<void>;
  refreshEmployee: () => Promise<void>;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);
const CRISP_WEBSITE_ID = "58834b46-3fbc-45c3-a132-7aa3d5786367";

export const EmployeeProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [employeeRole, setEmployeeRole] = useState<Enums<"app_role"> | null>(null);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [storeSubscriptionStatus, setStoreSubscriptionStatus] = useState<SubscriptionStatus>("active");
  const [isLoading, setIsLoading] = useState(true);
  // Lecture de `user_roles` en échec : le rôle est *inconnu*, ce qui n'est pas
  // la même chose qu'absent. Voir `loadEmployeeData`.
  const [sessionLoadError, setSessionLoadError] = useState(false);
  // Session d'un gérant (rôle franchisé, aucune fiche salarié) arrivée sur
  // l'espace salarié : écran qui nomme le compte, au lieu du renvoi silencieux
  // vers /dashboard. Voir la branche franchisé de `loadEmployeeData`.
  const [managerSession, setManagerSession] = useState<{ displayName: string | null } | null>(null);

  const loadEmployeeData = useCallback(async (userId: string) => {
    try {
      // Check if user has employee-type role
      const { data: rolesData, error: roleError } = await fetchUserRoles(userId);

      // Même distinction que pour `employeeError` plus bas : une erreur de
      // lecture n'est pas une réponse. Regrouper `roleError` avec
      // `!hasEmployeeRole` faisait qu'un réseau coupé — un salarié en réserve,
      // dans le métro — se lisait « cette personne n'est pas une salariée » et
      // l'envoyait sur /dashboard. StoreProvider la reprenait alors sans rôle
      // connu, la traitait en franchisée sans magasin et appelait
      // `repair-account`. On reste ici, avec un écran de reprise.
      if (roleError) {
        reportError(roleError, "employee-context", { userId, phase: "fetch-user-roles" });
        setSessionLoadError(true);
        return;
      }

      const employeeRoles = ["employee", "assistant_manager", "department_head", "franchisee"];
      const hasEmployeeRole = rolesData?.some(r => employeeRoles.includes(r.role));
      if (!rolesData || !hasEmployeeRole) {
        // Réponse effective du serveur : cette personne n'a pas de rôle
        // donnant accès au portail salarié.
        logger.debug("User is not an employee, redirecting...");
        navigate("/dashboard");
        return;
      }

      setSessionLoadError(false);

      const roleFromDb = (rolesData?.[0]?.role ?? "employee") as Enums<"app_role">;

      // Get employee record linked to this user
      const { data: employeeData, error: employeeError } = await fetchEmployeeWithStoreInfo(userId);

      if (employeeError || !employeeData) {
        // Deux causes très différentes, deux portées de déconnexion.
        // Fiche absente sans erreur : accès révoqué à la date de sortie
        // (migration 20260830130000), la fiche devient invisible à son
        // titulaire — on révoque alors TOUTES les sessions, sinon le PWA
        // installé sur le téléphone survivrait à la sortie.
        // Erreur de lecture (réseau coupé en réserve, PostgREST indisponible) :
        // déconnexion locale seulement. Couper les autres appareils d'un
        // salarié en poste pour une panne passagère serait disproportionné.
        // Cas légitime à écarter d'abord : le gérant propriétaire n'a jamais de
        // fiche salarié. Une session gérant ici, c'est presque toujours un lien
        // « Voir mes horaires » ouvert sur un appareil où le gérant est resté
        // connecté (téléphone d'un proche salarié, poste du magasin). On le
        // renvoyait vers /dashboard sans un mot : la personne se retrouvait
        // devant le tableau de bord du gérant et le vivait comme « j'ai accès
        // à son compte ». On nomme désormais le compte connecté et on laisse
        // choisir : se déconnecter, ou rejoindre l'espace gérant.
        if (roleFromDb === "franchisee") {
          const { data: profileData } = await fetchProfileMaybe(userId);
          const displayName = [profileData?.first_name, profileData?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
          setManagerSession({ displayName: displayName.length > 0 ? displayName : null });
          return;
        }
        const isRevoked = !employeeError && !employeeData;
        logger.error("Employee data not found, signing out to prevent loop", employeeError);
        await signOut(isRevoked ? "global" : "local");
        await clearAccountScopedCaches(queryClient);
        navigate("/auth");
        return;
      }

      setManagerSession(null);
      setEmployee(employeeData);
      setEmployeeRole(roleFromDb);

      const rawStores = employeeData.stores as unknown as
        | { name?: string; subscription_status?: string }
        | { name?: string; subscription_status?: string }[]
        | null;
      const storeData = Array.isArray(rawStores) ? rawStores[0] : rawStores;
      const rawStatus = storeData?.subscription_status;
      const validStatuses: SubscriptionStatus[] = [
        "active",
        "trial",
        "grace",
        "archived",
        "pending_subscription",
      ];
      setStoreSubscriptionStatus(
        rawStatus && validStatuses.includes(rawStatus as SubscriptionStatus)
          ? (rawStatus as SubscriptionStatus)
          : "active",
      );
      const n = storeData?.name;
      setStoreName(typeof n === "string" && n.length > 0 ? n : null);

      // Identify user in PostHog with stable context (role + store).
      // This is retriggered on future opt-in events as well.
      identifyPosthog(userId, {
        role: roleFromDb,
        store_id: employeeData.store_id ?? null,
      });

      setSentryUser({ id: userId, role: roleFromDb });
      setSentryContext("store", employeeData.store_id ? { id: employeeData.store_id, name: n ?? null } : null);

    } catch (error) {
      logger.error("Error loading employee data", error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, queryClient]);

  useEffect(() => {
    const { data: { subscription } } = onAuthStateChange(
      async (event, session) => {
        if (!session?.user) {
          navigate("/auth");
        } else {
          setUser(session.user);
          // Use setTimeout to avoid potential auth state race condition
          setTimeout(() => loadEmployeeData(session.user.id), 0);
        }
      }
    );

    getSession().then(({ data: { session } }) => {
      if (!session?.user) {
        navigate("/auth");
      } else {
        setUser(session.user);
        loadEmployeeData(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, loadEmployeeData]);

  // If analytics consent is accepted later, re-identify with the latest context.
  useEffect(() => {
    if (!user?.id || !employee) return;

    const distinctId = user.id;
    const handler = () => {
      identifyPosthog(distinctId, {
        role: employeeRole,
        store_id: employee?.store_id ?? null,
      });
    };

    window.addEventListener(POSTHOG_OPT_IN_EVENT, handler);
    return () => window.removeEventListener(POSTHOG_OPT_IN_EVENT, handler);
  }, [user?.id, employee, employeeRole]);

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      disableCrisp();
      return;
    }

    if (!canUseCrisp(employeeRole)) {
      disableCrisp();
      return;
    }

    const displayName =
      [employee?.first_name, employee?.last_name].filter(Boolean).join(" ").trim() || null;

    enableCrisp(CRISP_WEBSITE_ID, {
      email: user.email ?? null,
      displayName,
      role: employeeRole,
      storeName,
    });
  }, [isLoading, user, user?.email, employeeRole, employee?.first_name, employee?.last_name, storeName]);

  const handleLogout = async () => {
    disableCrisp();
    setSentryUser(null);
    await signOut("global");
    await clearAccountScopedCaches(queryClient);
    navigate("/auth?logged_out=true", { replace: true });
  };

  const refreshEmployee = useCallback(async () => {
    if (!user?.id) return;
    const { data, error } = await fetchEmployeeWithStoreInfo(user.id);
    if (error || !data) {
      logger.error("Failed to refresh employee data", error);
      return;
    }
    setEmployee(data);
    const rawStores = data.stores as unknown as
      | { name?: string; subscription_status?: string }
      | { name?: string; subscription_status?: string }[]
      | null;
    const storeData = Array.isArray(rawStores) ? rawStores[0] : rawStores;
    const n = storeData?.name;
    setStoreName(typeof n === "string" && n.length > 0 ? n : null);
  }, [user?.id]);

  return (
    <EmployeeContext.Provider
      value={{
        user,
        employee,
        userRole: employeeRole,
        storeName,
        storeSubscriptionStatus,
        isLoading,
        handleLogout,
        refreshEmployee,
      }}
    >
      {isLoading ? (
        <EmployeeDashboardSkeleton />
      ) : sessionLoadError ? (
        <SessionLoadError onLogout={handleLogout} />
      ) : managerSession ? (
        <ManagerOnEmployeePortal
          email={user?.email ?? null}
          displayName={managerSession.displayName}
          onGoToDashboard={() => navigate("/dashboard")}
          onLogout={handleLogout}
        />
      ) : (
        children
      )}
    </EmployeeContext.Provider>
  );
};

export const useEmployeeContext = () => {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error("useEmployeeContext must be used within an EmployeeProvider");
  }
  return context;
};
